import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { HeroTeaseProps } from "design-system/components/blocks/hero-tease/hero-tease";
import { createBylineProps } from "../../primitives/byline.props";
import { RenderContentfulRichText } from "../../utils/render-contentful-rich-text";
import { useSearch } from "frontend/hooks/use-search";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  ArticlePageEntry,
  DetailPageEntry,
  LandingPageEntry,
} from "frontend/contentful/schema/pages";
import { QueryEntry } from "frontend/contentful/schema/semantics";
import { createUseSearchProps } from "../../utils/use-search.props";
import { createSearchHeroTease } from "frontend/contentful/search/search-hero-tease";
import { SearchQuery, ModernSearchHit, LegacySearchHit } from "frontend/types";
import { useContentful } from "frontend/hooks/use-contentful";
import { createMediaAssetProps } from "../../primitives/media-asset.props";

export const createHeroTeaseProps = createContentfulProps<
  ArticlePageEntry | QueryEntry | LandingPageEntry | DetailPageEntry,
  HeroTeaseProps
>(({ entry, createEditAttributes }) => {
  const { getEntry, getEntryUrl } = useContentful();
  let searchProps: SearchQuery | undefined;
  const isQueryEntry = isContentType<QueryEntry>("query", entry);

  /* QueryEntry */
  if (isQueryEntry) {
    const base = createUseSearchProps(entry);
    searchProps = base ? { ...base, size: 1 } : undefined;
  }

  const search = useSearch<LegacySearchHit | ModernSearchHit>(searchProps, {
    enabled: isQueryEntry ? true : false,
  });

  if (search?.data?.hits?.[0]) {
    const searchHeroTeaseProps = createSearchHeroTease(search?.data?.hits?.[0]);
    if (searchHeroTeaseProps) {
      return searchHeroTeaseProps;
    }
  }

  /* ArticlePageEntry */
  if (isContentType<ArticlePageEntry>("articlePage", entry)) {
    const props: HeroTeaseProps = {
      title: entry.fields.title,
      media: createMediaAssetProps(entry.fields.leadImage),
      link: getEntryUrl(entry),
      byline: createBylineProps(entry),
      subheading: (
        <RenderContentfulRichText
          document={entry.fields.shortDescription || entry.fields.description}
          disableParagraphs
        />
      ),
    };

    if (props.byline) props.byline.headshots = true;

    const overline = getEntry(entry.fields.overline);

    if (overline?.fields.text && overline.fields.url) {
      props.overline = {
        label: overline.fields.text,
        link: getEntryUrl(overline),
      };
    } else if (entry.fields.topic?.length) {
      const topicEntry = getEntry(entry.fields.topic[0]);
      if (topicEntry?.fields.title) {
        props.overline = {
          label: topicEntry?.fields.title,
        };
      }
    }

    props.editAttributes = {
      subheading: createEditAttributes({
        entry,
        fieldId: "shortDescription",
      }),
      overline: createEditAttributes({
        entry: overline,
        fieldId: "text",
      }),
    };

    return props;
  }

  /* LandingPageEntry */
  if (isContentType<LandingPageEntry>("landingPage", entry)) {
    return {
      title: entry.fields.title,
      link: getEntryUrl(entry),
    };
  }

  /* DetailPageEntry */
  if (isContentType<DetailPageEntry>("detailPage", entry)) {
    return {
      title: entry.fields.title,
      link: getEntryUrl(entry),
    };
  }
  return undefined;
});
